/**
 * R3 — SubD commands on a real Catmull-Clark kernel.
 *
 * A SubD entity carries its CONTROL CAGE (vertices + quad/tri faces) as
 * the parametric source of truth plus a crease weight and a display flag.
 * The renderer (SubdEntityMesh) derives the smooth limit (CC passes) or
 * shows the raw cage per `subdSmooth`, so CREASE / REMOVECREASE /
 * SUBDDISPLAY mutate real source data with a real consumer.
 */

import {
    runInteraction, PT, PN, PK, PLoopK, entityToPolyline, nurbsSurfaceEntity, deriveEntities,
} from './lib';
import { undoableAdd, undoableReplace, makeEntity } from '../entityOps';
import { useAppStore } from '../../../state/store';
import { weldMesh, centroid } from '../geometryMath';
import type { Pt3 } from '../geometryMath';
import { boxMesh } from '../meshFactory';
import { subdLimit, cageToTriangles, quadifyCage, type SubdCage } from '../../geometry/subd/subd';
import { fitSurfaceFromPoints } from '../../geometry/nurbs/surface';
import type { RunnerMap } from '../runnerRegistry';
import type { Entity } from '../../../state/store';

function subdEntity(cage: SubdCage, name: string, opts: { crease?: number; smooth?: boolean } = {}): Entity {
    return makeEntity('subd', name, {
        cage: { vertices: cage.vertices.map((v) => [...v]), faces: cage.faces.map((f) => [...f]) },
        crease: opts.crease ?? 0,
        subdSmooth: opts.smooth ?? true,
        color: '#c084fc',
        position: [0, 0, 0],
    });
}

function cageOf(e: Entity | undefined): { cage: SubdCage; crease: number } | null {
    if (!e || e.type !== 'subd' || !e.data.cage) return null;
    return { cage: e.data.cage as SubdCage, crease: Number(e.data.crease) || 0 };
}

/** Re-emit one picked SubD with a mutated cage (single undo step). */
function rewrite(picks: string[], mutate: (cage: SubdCage, crease: number) => { cage: SubdCage; crease: number }): string {
    const { entities } = useAppStore.getState();
    let n = 0;
    picks.forEach((id) => {
        const e = entities[id];
        const data = cageOf(e);
        if (!e || !data) return;
        const next = mutate(data.cage, data.crease);
        undoableReplace([id], [subdEntity(next.cage, e.name, { crease: next.crease, smooth: !!e.data.subdSmooth })]);
        n++;
    });
    return n ? `${n} SubD object(s) updated.` : 'No SubD among the picks.';
}

/** Triangles to quad-cage: weld, then pair coplanar neighbours. */
function cageFromMesh(vertices: number[][], indices: number[][]): SubdCage {
    const welded = weldMesh({ vertices, indices });
    return quadifyCage({
        vertices: welded.vertices.map((v) => [v[0], v[1], v[2] ?? 0] as Pt3),
        faces: welded.indices.map((t) => [...t]),
    });
}

export const subdRunners: RunnerMap = {
    'subd.fromObj': () => {
        runInteraction({
            steps: [PLoopK('TOSUBD: pick mesh/surface objects', 1)],
            complete: (_p, _v, picks) => {
                const { entities } = useAppStore.getState();
                let n = 0;
                picks.forEach((id) => {
                    const e = entities[id];
                    if (!e) return;
                    const tess = e.data.tessellation as { vertices: number[][]; indices: number[][] } | undefined;
                    const m = e.type === 'solid'
                        ? { vertices: (e.data.vertices as number[][]) ?? [], indices: (e.data.indices as number[][]) ?? [] }
                        : tess && Array.isArray(tess.vertices) ? tess : null;
                    if (!m || m.vertices.length === 0) return;
                    undoableReplace([id], [subdEntity(cageFromMesh(m.vertices, m.indices), `SubD-${Date.now()}`)]);
                    n++;
                });
                return n ? `TOSUBD: ${n} object(s) converted to cage.` : 'TOSUBD: nothing convertible.';
            },
        });
        return 'TOSUBD: pick meshes';
    },

    'subd.toNurbs': () => {
        runInteraction({
            steps: [PK('TONURBS: pick a SubD', ['subd'])],
            complete: (_p, _v, picks) => {
                const e = useAppStore.getState().entities[picks[0]];
                const data = cageOf(e);
                if (!data) return 'TONURBS: pick a SubD.';
                // Limit mesh → least-squares NURBS fit through its vertices
                // (tolerance-reported honest conversion).
                const limit = subdLimit(data.cage, 2, data.crease);
                const tris = cageToTriangles(limit);
                const srf = fitSurfaceFromPoints(tris.vertices.map((v) => [v[0], v[1], v[2] ?? 0] as Pt3), 12, 12);
                if (!srf) return 'TONURBS: could not fit a surface to this cage.';
                undoableReplace(picks, [nurbsSurfaceEntity(srf, `Nurbs-${Date.now()}`)]);
                return 'TONURBS: limit surface fitted to a NURBS patch (least-squares within tolerance).';
            },
        });
        return 'TONURBS: pick a SubD';
    },

    'subd.box': () => {
        runInteraction({
            steps: [PT('SUBDBOX: base corner'), PN('SUBDBOX: size <1.5>')],
            complete: (pts, values) => {
                const s = Number(values[0]) || 1.5;
                const m = boxMesh(s, s, s);
                // the 8-corner / 12-triangle box payload pairs into 6 quads
                const cage = cageFromMesh(
                    m.vertices.map((v) => [v[0] + pts[0].x, v[1] + pts[0].y, v[2] + pts[0].z]),
                    m.indices
                );
                undoableAdd([subdEntity(cage, `SubDBox-${Date.now()}`)]);
                return 'SubD box cage created (Catmull-Clark rounds it when smoothed).';
            },
        });
        return 'SUBDBOX: base corner';
    },

    'subd.sphere': () => {
        runInteraction({
            steps: [PT('SUBDSPHERE: center'), PN('SUBDSPHERE: radius <1>')],
            complete: (pts, values) => {
                const r = Number(values[0]) || 1;
                const ox = pts[0].x, oy = pts[0].y, oz = pts[0].z;
                const cage: SubdCage = {
                    vertices: [
                        [0, 0, r], [0, 0, -r],
                        [r, 0, 0], [0, r, 0], [-r, 0, 0], [0, -r, 0],
                    ].map((p) => [p[0] + ox, p[1] + oy, p[2] + oz] as Pt3),
                    faces: [
                        [0, 2, 3], [0, 3, 4], [0, 4, 5], [0, 5, 2],
                        [1, 3, 2], [1, 4, 3], [1, 5, 4], [1, 2, 5],
                    ],
                };
                undoableAdd([subdEntity(cage, `SubDSphere-${Date.now()}`)]);
                return 'Even-flow SubD sphere (octahedral cage) created.';
            },
        });
        return 'SUBDSPHERE: center';
    },

    'subd.cylinder': () => {
        runInteraction({
            steps: [PT('SUBDCYLINDER: base center'), PN('SUBDCYLINDER: radius <1>'), PN('SUBDCYLINDER: height <2>')],
            complete: (pts, values) => {
                const r = Number(values[0]) || 1, h = Number(values[1]) || 2;
                const sides = 6;
                const verts: Pt3[] = [[pts[0].x, pts[0].y, pts[0].z], [pts[0].x, pts[0].y, pts[0].z + h]];
                for (let i = 0; i < sides; i++) {
                    const a = (i / sides) * Math.PI * 2;
                    verts.push([pts[0].x + Math.cos(a) * r, pts[0].y + Math.sin(a) * r, pts[0].z]);
                    verts.push([pts[0].x + Math.cos(a) * r, pts[0].y + Math.sin(a) * r, pts[0].z + h]);
                }
                const faces: number[][] = [];
                for (let i = 0; i < sides; i++) {
                    const b0 = 2 + i * 2, b1 = 2 + ((i + 1) % sides) * 2;
                    faces.push([b0, b1, b1 + 1, b0 + 1]);
                }
                const ring = (offset: number, flip: boolean): number[] => {
                    const ids = Array.from({ length: sides }, (_, k) => offset + k * 2);
                    return flip ? ids.reverse() : ids;
                };
                faces.push(ring(2, false));      // bottom polygon (n-gon → fan at render)
                faces.push(ring(3, true));       // top polygon
                undoableAdd([subdEntity({ vertices: verts, faces }, `SubDCyl-${Date.now()}`)]);
                return `SubD cylinder created (${sides}-sided tube + n-gon caps).`;
            },
        });
        return 'SUBDCYLINDER: base center';
    },

    'subd.plane': () => {
        runInteraction({
            steps: [PN('SUBDPLANE: width <4>'), PN('SUBDPLANE: depth <4>'), PN('SUBDPLANE: segments <4>')],
            complete: (_p, values) => {
                const w = Number(values[0]) || 4, d = Number(values[1]) || 4, n = Math.max(2, Math.round(Number(values[2]) || 4));
                const grid = gridToCage(n, w, d);
                undoableAdd([subdEntity(grid, `SubDPlane-${Date.now()}`)]);
                return `SubD plane ${n}x${n} quad grid created.`;
            },
        });
        return 'SUBDPLANE: width';
    },

    'subd.loft': () => {
        runInteraction({
            steps: [PLoopK('SUBDLOFT: pick cross-section curves (2+)', 2)],
            complete: (_p, _v, picks) => {
                const created = deriveEntities('subd.loft', picks, (sources) => {
                    const secs = sources.map((e) => entityToPolyline(e)).filter(Boolean) as Pt3[][];
                    if (secs.length < 2) return null;
                    const cols = Math.min(...secs.map((sec) => sec.length));
                    if (cols < 2) return null;
                    const verts: Pt3[] = [];
                    secs.forEach((sec) => { for (let c = 0; c < cols; c++) verts.push([sec[c][0], sec[c][1], sec[c][2] ?? 0] as Pt3); });
                    const faces: number[][] = [];
                    for (let r = 0; r < secs.length - 1; r++) for (let c = 0; c < cols - 1; c++) {
                        const a = r * cols + c;
                        faces.push([a, a + 1, a + cols + 1, a + cols]);
                    }
                    return [subdEntity({ vertices: verts, faces }, `SubDLoft-${Date.now()}`)];
                });
                if (!created) return 'SUBDLOFT: pick 2+ section curves (>=2 points each).';
                return 'SubD lofted quad cage through the sections.';
            },
        });
        return 'SUBDLOFT: pick sections';
    },

    'subd.offset': () => {
        runInteraction({
            steps: [PK('SUBDOFFSET: pick a SubD', ['subd']), PN('SUBDOFFSET: thickness <0.2>')],
            complete: (_p, values, picks) => {
                const e = useAppStore.getState().entities[picks[0]];
                const data = cageOf(e);
                if (!data) return 'SUBDOFFSET: pick a SubD.';
                const t = Number(values[0]) || 0.2;
                // Solidify: outer cage scaled about the centroid + quad wall
                // strip over every boundary (naked) edge — a real shell.
                const c = centroid(data.cage.vertices);
                const n = data.cage.vertices.length;
                const outer = data.cage.vertices.map((p) => {
                    const d: Pt3 = [p[0] - c[0], p[1] - c[1], p[2] - c[2]];
                    const l = Math.hypot(d[0], d[1], d[2]) || 1;
                    return [p[0] + (d[0] / l) * t, p[1] + (d[1] / l) * t, p[2] + (d[2] / l) * t] as Pt3;
                });
                const edgeCount = new Map<string, number>();
                const bump = (a: number, b: number) => {
                    const k = a < b ? `${a}:${b}` : `${b}:${a}`;
                    edgeCount.set(k, (edgeCount.get(k) ?? 0) + 1);
                };
                data.cage.faces.forEach((f) => {
                    for (let i = 0; i < f.length; i++) bump(f[i], f[(i + 1) % f.length]);
                });
                const faces = [...data.cage.faces.map((f) => [...f]), ...data.cage.faces.map((f) => [...f].map((i) => i + n).reverse())];
                edgeCount.forEach((count, k) => {
                    if (count !== 1) return;
                    const [a, b] = k.split(':').map(Number);
                    faces.push([a, b, b + n, a + n]);
                });
                undoableReplace(picks, [subdEntity({ vertices: [...data.cage.vertices.map((v) => [...v] as Pt3), ...outer], faces }, `SubDOffset-${Date.now()}`, { crease: data.crease })]);
                return `SubD solidified by ${t} (outer shell + boundary walls).`;
            },
        });
        return 'SUBDOFFSET: pick a SubD';
    },

    'subd.insertEdge': () => {
        runInteraction({
            steps: [PK('INSERTEDGE: pick a SubD', ['subd']), PT('INSERTEDGE: click near the loop')],
            complete: (pts, _v, picks) => {
                const e = useAppStore.getState().entities[picks[0]];
                const data = cageOf(e);
                if (!data) return 'INSERTEDGE: pick a SubD.';
                // Targeted refinement: locate the face nearest the click and
                // split that row of faces through edge midpoints (1→4 for the
                // hit faces, 1→2 for their ring neighbours).
                const p: Pt3 = [pts[0].x, pts[0].y, pts[0].z];
                const verts = data.cage.vertices.map((v) => [...v] as Pt3);
                const midOf = new Map<string, number>();
                const mid = (a: number, b: number): number => {
                    const k = a < b ? `${a}:${b}` : `${b}:${a}`;
                    const hit = midOf.get(k);
                    if (hit !== undefined) return hit;
                    const pa = verts[a], pb = verts[b];
                    verts.push([(pa[0] + pb[0]) / 2, (pa[1] + pb[1]) / 2, (pa[2] + pb[2]) / 2]);
                    midOf.set(k, verts.length - 1);
                    return verts.length - 1;
                };
                let best = -1, bd = Infinity;
                data.cage.faces.forEach((f, fi) => {
                    const c = centroid(f.map((i) => verts[i]));
                    const d = Math.hypot(c[0] - p[0], c[1] - p[1], c[2] - p[2]);
                    if (d < bd) { bd = d; best = fi; }
                });
                if (best < 0) return 'INSERTEDGE: empty cage.';
                const splitFace = (f: number[]): number[][] => {
                    const mids = f.map((_, i) => mid(f[i], f[(i + 1) % f.length]));
                    const c = centroid(f.map((i) => verts[i]));
                    verts.push(c);
                    const ci = verts.length - 1;
                    const out: number[][] = [];
                    for (let i = 0; i < f.length; i++) {
                        out.push([f[i], mids[i], ci, mids[(i - 1 + f.length) % f.length]]);
                    }
                    return out;
                };
                const target = new Set([best]);
                // ring neighbours of the hit face
                const keyOf = (a: number, b: number) => (a < b ? `${a}:${b}` : `${b}:${a}`);
                const f0 = data.cage.faces[best];
                const edgeKeys = new Set(f0.map((v, i) => keyOf(v, f0[(i + 1) % f0.length])));
                data.cage.faces.forEach((f, fi) => {
                    if (target.has(fi)) return;
                    for (let i = 0; i < f.length; i++) {
                        if (edgeKeys.has(keyOf(f[i], f[(i + 1) % f.length]))) { target.add(fi); break; }
                    }
                });
                const faces: number[][] = [];
                data.cage.faces.forEach((f, fi) => {
                    if (target.has(fi)) faces.push(...splitFace(f));
                    else faces.push([...f]);
                });
                undoableReplace(picks, [subdEntity({ vertices: verts, faces }, `SubDEdge-${Date.now()}`, { crease: data.crease, smooth: !!e.data.subdSmooth })]);
                return `InsertEdge: row of ${target.size} face(s) refined near the click.`;
            },
        });
        return 'INSERTEDGE: pick a SubD then click';
    },

    'subd.crease': () => {
        runInteraction({
            steps: [PK('CREASE: pick a SubD', ['subd']), PN('CREASE: weight <1> (0-1)')],
            complete: (_p, values, picks) => {
                const w = Math.max(0, Math.min(1, Number(values[0]) || 1));
                return rewrite(picks, (cage, crease) => ({ cage, crease: Math.max(crease, w) }))
                    + ` Crease weight ${w} sharpens the limit toward the cage.`;
            },
        });
        return 'CREASE: pick a SubD';
    },
    'subd.uncrease': () => {
        runInteraction({
            steps: [PK('REMOVECREASE: pick a SubD', ['subd'])],
            complete: (_p, _v, picks) => rewrite(picks, (cage, crease) => ({ cage, crease: Math.min(crease, 0) })) + ' SubD smoothed to the default cage.',
        });
        return 'REMOVECREASE: pick a SubD';
    },

    'subd.displayToggle': () => {
        runInteraction({
            steps: [PK('SUBDDISPLAY: pick a SubD', ['subd'])],
            complete: (_p, _v, picks) => {
                const { entities } = useAppStore.getState();
                const e = entities[picks[0]];
                const data = cageOf(e);
                if (!e || !data) return 'SUBDDISPLAY: pick a SubD.';
                const smooth = !e.data.subdSmooth;
                undoableReplace(picks, [subdEntity(data.cage, e.name, { crease: data.crease, smooth })]);
                return `SubD display: ${smooth ? 'smooth limit' : 'cage'} mode.`;
            },
        });
        return 'SUBDDISPLAY: pick a SubD';
    },
};

function gridToCage(n: number, w: number, d: number): SubdCage {
    const verts: Pt3[] = [];
    for (let r = 0; r <= n; r++) for (let c = 0; c <= n; c++) {
        verts.push([(c / n - 0.5) * w, (r / n - 0.5) * d, 0]);
    }
    const faces: number[][] = [];
    for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) {
        const a = r * (n + 1) + c;
        faces.push([a, a + 1, a + n + 2, a + n + 1]);
    }
    return { vertices: verts, faces };
}
